import {
  CalendarSync,
  CarFront,
  ExternalLink,
  type LucideIcon,
  Undo2,
} from "lucide-react";
import type { BadgeProps } from "@rumbledirect/ui-v2";
import type {
  ContractExpirationDecision,
  ContractExpirationDecisionQueryValue,
  ContractExpirationStatus,
  ContractExpirationStatusFilter,
  ContractExpirationStatusQueryValue,
} from "./contractExpiration";

/**
 * Dutch labels for how far the handling has come, mirroring the backend's
 * `ContractExpirationStatus` so both surfaces name the same state the same way.
 */
export const CONTRACT_EXPIRATION_STATUS_LABELS: Record<
  ContractExpirationStatus,
  string
> = {
  signalled: "Gesignaleerd",
  asked: "Klant gevraagd",
  in_progress: "In behandeling",
  completed: "Afgerond",
};

/**
 * Badge tone per status, read as a progression: an untouched signal is merely
 * information, a pending answer is a warning because the clock is running, and
 * a settled expiration is done.
 */
export const CONTRACT_EXPIRATION_STATUS_VARIANTS: Record<
  ContractExpirationStatus,
  NonNullable<BadgeProps["variant"]>
> = {
  signalled: "info",
  asked: "warning",
  in_progress: "progress",
  completed: "done",
};

/**
 * Dutch labels for the follow-up, mirroring the backend's
 * `ContractExpirationDecision`.
 *
 * Named after what happens to the vehicle rather than after the channel it is
 * arranged through, so the wording survives a customer that orders elsewhere.
 *
 * Phrased as the intention and not as the deed, because that is what a recorded
 * choice is: the renewal still has to be drawn up and the vehicle still has to
 * come back. Reading "Voertuig ingeleverd" beside a car that is still on the
 * road is how a work list loses its authority.
 */
export const CONTRACT_EXPIRATION_DECISION_LABELS: Record<
  ContractExpirationDecision,
  string
> = {
  renewal: "Contract verlengen",
  return: "Voertuig inleveren",
  replacement_via_configurator: "Vervangen via configurator",
  replacement_external: "Vervangen buiten configurator",
};

/**
 * What each follow-up means for the vehicle, one line per choice.
 *
 * The labels name the act, which is enough on a badge but not enough at the
 * moment of choosing: "Vervangen via configurator" and "Vervangen buiten
 * configurator" differ only in a preposition, while what happens next differs
 * entirely. Spelling out the consequence is cheaper than a wrong choice that
 * has to be undone by hand somewhere else in the application.
 */
export const CONTRACT_EXPIRATION_DECISION_DESCRIPTIONS: Record<
  ContractExpirationDecision,
  string
> = {
  renewal: "Dezelfde auto blijft rijden, op een verlengd of nieuw contract.",
  return:
    "De auto gaat terug naar de leasemaatschappij en er komt niets voor in de plaats.",
  replacement_via_configurator:
    "De medewerker stelt zelf een opvolger samen; wij sturen daarvoor een uitnodiging.",
  replacement_external:
    "Er komt een opvolger, maar die wordt buiten de configurator om besteld.",
};

/**
 * An icon per follow-up, so the four choices are told apart before they are
 * read. Chosen for what happens to the vehicle: the same one that keeps
 * driving, one that goes back, one that is put together here, one that is
 * arranged elsewhere.
 */
export const CONTRACT_EXPIRATION_DECISION_ICONS: Record<
  ContractExpirationDecision,
  LucideIcon
> = {
  renewal: CalendarSync,
  return: Undo2,
  replacement_via_configurator: CarFront,
  replacement_external: ExternalLink,
};

/**
 * The statuses offered in the filter, opening with the reading the work list
 * exists for and followed by the individual states in the order they occur.
 */
export const CONTRACT_EXPIRATION_STATUS_FILTER_OPTIONS: readonly {
  readonly value: ContractExpirationStatusFilter;
  readonly label: string;
}[] = [
  { value: "open", label: "Openstaand" },
  { value: "signalled", label: CONTRACT_EXPIRATION_STATUS_LABELS.signalled },
  { value: "asked", label: CONTRACT_EXPIRATION_STATUS_LABELS.asked },
  {
    value: "in_progress",
    label: CONTRACT_EXPIRATION_STATUS_LABELS.in_progress,
  },
  { value: "completed", label: CONTRACT_EXPIRATION_STATUS_LABELS.completed },
];

/** The follow-ups offered in the filter, in the same order as the enum. */
export const CONTRACT_EXPIRATION_DECISION_FILTER_OPTIONS: readonly {
  readonly value: ContractExpirationDecisionQueryValue;
  readonly label: string;
}[] = [
  { value: "renewal", label: CONTRACT_EXPIRATION_DECISION_LABELS.renewal },
  { value: "return", label: CONTRACT_EXPIRATION_DECISION_LABELS.return },
  {
    value: "replacement_via_configurator",
    label: CONTRACT_EXPIRATION_DECISION_LABELS.replacement_via_configurator,
  },
  {
    value: "replacement_external",
    label: CONTRACT_EXPIRATION_DECISION_LABELS.replacement_external,
  },
];

/** The three statuses that still need a person to do something. */
const OPEN_STATUSES: readonly ContractExpirationStatusQueryValue[] = [
  "signalled",
  "asked",
  "in_progress",
];

/**
 * Translates the filter choice into the statuses the backend is asked for.
 *
 * "Openstaand" is the only choice that widens to more than one status; the rest
 * map one-to-one, and an absent filter asks for nothing so every status shows.
 */
export function toExpirationStatusQuery(
  filter: ContractExpirationStatusFilter | undefined,
): readonly ContractExpirationStatusQueryValue[] | undefined {
  if (filter === undefined) return undefined;

  return filter === "open" ? OPEN_STATUSES : [filter];
}

/** Type guard for a status filter coming out of the URL's search params. */
export function isContractExpirationStatusFilter(
  value: unknown,
): value is ContractExpirationStatusFilter {
  return CONTRACT_EXPIRATION_STATUS_FILTER_OPTIONS.some(
    (option) => option.value === value,
  );
}

/** Type guard for a decision filter coming out of the URL's search params. */
export function isContractExpirationDecisionFilter(
  value: unknown,
): value is ContractExpirationDecisionQueryValue {
  return CONTRACT_EXPIRATION_DECISION_FILTER_OPTIONS.some(
    (option) => option.value === value,
  );
}
